import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, FileText, ScrollText, Tags, Calculator, ReceiptText,
  FileCheck2, Wallet, HandCoins, GitCompareArrows, PackageCheck, BarChart3,
  Settings, History, Users, Building2, FolderKanban, Boxes, Truck, Undo2,
  LogOut, Menu, ChevronDown, FileWarning,
} from "lucide-react";

const NAV = [
  { section: "Utama", items: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  ]},
  { section: "Master Data", items: [
    { to: "/clients", label: "Klien", icon: Building2 },
    { to: "/projects", label: "Proyek", icon: FolderKanban },
    { to: "/materials", label: "Material", icon: Boxes },
    { to: "/contracts", label: "Kontrak", icon: ScrollText },
    { to: "/rates", label: "Tarif Material", icon: Tags },
  ]},
  { section: "Operasional Material", items: [
    { to: "/delivery-notes", label: "Surat Jalan", icon: Truck },
    { to: "/return-notes", label: "Surat Pengembalian", icon: Undo2 },
  ]},
  { section: "Penagihan", items: [
    { to: "/purchase-orders", label: "Purchase Order", icon: FileText },
    { to: "/generate", label: "Generate Tagihan", icon: Calculator },
    { to: "/proformas", label: "Proforma", icon: FileCheck2 },
    { to: "/invoices", label: "Invoice", icon: ReceiptText },
  ]},
  { section: "Keuangan", items: [
    { to: "/payments", label: "Pembayaran", icon: Wallet },
    { to: "/receivables", label: "Piutang", icon: HandCoins },
    { to: "/outstanding", label: "Outstanding", icon: FileWarning },
    { to: "/reconciliation", label: "Rekonsiliasi", icon: GitCompareArrows },
    { to: "/closing", label: "Closing Proyek", icon: PackageCheck },
  ]},
  { section: "Lain-lain", items: [
    { to: "/reports", label: "Laporan", icon: BarChart3 },
    { to: "/settings", label: "Pengaturan", icon: Settings },
    { to: "/audit", label: "Audit Log", icon: History },
    { to: "/users", label: "Pengguna", icon: Users },
  ]},
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#0e0e11] text-slate-300 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center gap-3 px-5 border-b border-white/10">
          <img src="/pcm-logo.png" alt="PCM" className="h-9 w-9 object-contain" />
          <div>
            <div className="font-display font-bold text-white leading-tight text-sm">CV Putra Cilegon Mandiri</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-violet-400">Scaffolding Rental</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto scrollbar-thin py-4 px-3 space-y-5">
          {NAV.map((sec) => (
            <div key={sec.section}>
              <div className="px-3 mb-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium">{sec.section}</div>
              <div className="space-y-0.5">
                {sec.items.map((it) => (
                  <NavLink key={it.to} to={it.to} end={it.end} onClick={() => setOpen(false)}
                    data-testid={`nav-${it.to === "/" ? "dashboard" : it.to.slice(1)}`}
                    className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive ? "bg-[#9333EA] text-white font-medium" : "hover:bg-white/5 hover:text-white"}`}>
                    <it.icon className="h-4 w-4 shrink-0" />
                    {it.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b bg-[#0e0e11]/80 backdrop-blur-xl sticky top-0 z-20 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="menu-toggle">
              <Menu className="h-5 w-5" />
            </Button>
            <div className="font-display font-bold text-white hidden sm:block">CV Putra Cilegon Mandiri</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium leading-tight">{user?.name}</div>
              <div className="text-[11px] text-muted-foreground uppercase tracking-wide">{roleLabel(user?.role)}</div>
            </div>
            <div className="h-9 w-9 rounded-full bg-[#9333EA] text-white flex items-center justify-center text-sm font-semibold">
              {(user?.name || "?").charAt(0)}
            </div>
            <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8 max-w-[1600px] w-full mx-auto">{children}</main>
      </div>
    </div>
  );
}

function roleLabel(r) {
  return { super_admin: "Super Admin", finance_admin: "Finance Admin", finance_approver: "Finance Approver", admin_logistik: "Admin Logistik", viewer: "Viewer" }[r] || r;
}
